import { wfetch } from "../utils/fetch";
import Dropdown from "../utils/classes/Dropdown";

class CertificatesOverviewDataService {
    constructor(restUrl) {
        // Detect if we need magnoliaAuthor context path for local development
        const currentPath = window.location.pathname;
        const hasMagnoliaAuthor = currentPath.includes('/magnoliaAuthor');
        const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        const isLocalDev = window.location.port === '8080' || isLocalhost;
        const contextPath = (hasMagnoliaAuthor || isLocalDev) ? '/magnoliaAuthor' : '';
        this.restUrl = contextPath + restUrl;
    }
    
    async fetchAllHolders(onProgress) {
        try {
            // Fetch all holders without limit
            let allHolders = [];
            let offset = 0;
            let hasMore = true;
            let batchCount = 0;
            const estimatedTotal = 1000; // Estimate for progress calculation
            
            // Initial progress
            if (onProgress) onProgress(0, 0, 'Fetching holders...');
            
            while (hasMore) {
                const url = `${this.restUrl}?offset=${offset}`;
                const response = await wfetch(url);
                
                if (response.status !== 200) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                
                // wfetch already parses JSON, data is in response.data
                const data = response.data;
                
                // Handle both array and object with results property
                const holders = Array.isArray(data) ? data : (data.results || []);
                
                if (holders.length === 0) {
                    hasMore = false;
                } else {
                    allHolders = allHolders.concat(holders);
                    offset += holders.length;
                    batchCount++;
                    
                    // Update progress (estimate based on fetched vs estimated)
                    if (onProgress) {
                        const progressPercent = Math.min(95, Math.round((allHolders.length / estimatedTotal) * 100));
                        onProgress(progressPercent, allHolders.length, `Fetching holders... (${allHolders.length} loaded)`);
                    }
                }
            }
            
            console.log('[CertificatesOverviewDataService] Total holders from API:', allHolders.length);
            
            // Log sample holders to understand structure
            const sampleHolder = allHolders.find(h => h['@nodes'] && h['@nodes'].length > 0);
            if (sampleHolder) {
                console.log('[CertificatesOverviewDataService] Sample holder WITH @nodes:', {
                    id: sampleHolder['@id'],
                    name: sampleHolder['@name'],
                    fullName: sampleHolder.fullName,
                    nodes: sampleHolder['@nodes'],
                    nodeTypes: sampleHolder['@nodes'].map(n => sampleHolder[n]?.['@nodeType']).filter(Boolean)
                });
            }
            
            // Update progress to processing phase
            if (onProgress) onProgress(95, allHolders.length, 'Processing certificates...');
            
            // Process holders and their certificates (this will filter out holders without certificates)
            const processed = this.processHoldersData(allHolders);
            console.log('[CertificatesOverviewDataService] Processed holders count (with certificates):', processed.length);
            console.log('[CertificatesOverviewDataService] Filtered out holders without certificates:', allHolders.length - processed.length);
            
            // Complete
            if (onProgress) onProgress(100, processed.length, 'Complete!');
            
            return processed;
        } catch (error) {
            console.error('[CertificatesOverviewDataService] Error fetching holders:', error);
            throw error;
        }
    }
    
    processHoldersData(holders) {
        const processed = [];
        const seenHolderIds = new Set(); // Track unique holders by ID
        
        holders.forEach(holder => {
            if (!holder || !holder.fullName) return;
            
            // Get unique identifier for holder
            const holderId = holder['@id'] || holder['@uuid'] || holder['@name'];
            
            // Skip if we've already processed this holder
            if (seenHolderIds.has(holderId)) {
                console.warn('[CertificatesOverviewDataService] Duplicate holder detected:', holderId, holder.fullName);
                return;
            }
            
            seenHolderIds.add(holderId);
            
            // Get certificates - handle both array and single object
            const certificates = this.extractCertificates(holder);
            
            if (certificates.length === 0) return;
            
            // Console log to inspect certificate structure (only for first certificate of first holder)
            if (processed.length === 0 && certificates.length > 0 && certificates[0].examAssessmentTraining) {
                console.log('[CertificatesOverviewDataService] Certificate examAssessmentTraining structure:', certificates[0].examAssessmentTraining);
            }
            
            processed.push({
                holder: {
                    id: holderId,
                    name: holder['@name'],
                    fullName: holder.fullName,
                    email: holder.email,
                    company: holder.partnerInContentApp || holder.companyName,
                    companyName: holder.companyName,
                    partnerCompany: holder.partnerCompany,
                    country: holder.country
                },
                certificates: certificates.map(cert => ({
                    id: cert['@id'] || cert['@uuid'],
                    name: cert['@name'],
                    title: (() => {
                        const value = cert.certificateTitle || cert.title;
                        if (!value) return value;
                        if (typeof value === 'object') {
                            return value.displayName || value.name || value['@name'];
                        }
                        return value;
                    })(),
                    type: cert.certificateEarned,
                    programType: cert.examAssessmentTraining, // This should now be the full category object
                    issueDate: cert.certificateIssueDate || cert.issueDate,
                    expiryDate: cert.certificateExpiryDate,
                    additionalInfo: cert.additionalInfo,
                    // Check publication status using Magnolia system properties
                    // mgnl:lastActivated exists if the node has been published at least once
                    // mgnl:activationStatus can be 0 (not activated), 1 (activated), 2 (modified)
                    published: cert['mgnl:lastActivated'] ? true : false,
                    activationStatus: cert['mgnl:activationStatus'],
                    lastActivated: cert['mgnl:lastActivated'],
                    magnoliaVersion: cert.magnoliaVersion
                }))
            });
        });
        
        console.log('[CertificatesOverviewDataService] Unique holders processed:', processed.length);
        console.log('[CertificatesOverviewDataService] Total certificates across all holders:', processed.reduce((sum, h) => sum + h.certificates.length, 0));
        
        return processed;
    }
    
    extractCertificates(holder) {
        if (!holder) return [];
        
        let certificates = [];
        
        // Extract certificates from @nodes array (direct child nodes of holder)
        // Certificates are listed in @nodes array like ["xx", "xx1"] and accessible as holder.xx, holder.xx1, etc.
        if (holder['@nodes'] && Array.isArray(holder['@nodes']) && holder['@nodes'].length > 0) {
            certificates = holder['@nodes']
                .map(nodeName => holder[nodeName])
                .filter(cert => cert && cert['@nodeType'] === 'certificate'); // Only include certificate nodes
        }
        // Fallback: Extract certificates from newCerts (alternative structure)
        else if (holder.newCerts && holder.newCerts['@nodes']) {
            certificates = holder.newCerts['@nodes']
                .map(key => holder.newCerts[key])
                .filter(cert => cert && cert['@nodeType'] === 'certificate');
        }
        // Fallback: Extract from certificates property with @nodes
        else if (holder.certificates && holder.certificates['@nodes']) {
            certificates = holder.certificates['@nodes']
                .map(key => holder.certificates[key])
                .filter(cert => cert && cert['@nodeType'] === 'certificate');
        }
        // Fallback: Direct certificates array or object
        else {
            const certs = holder.certificates || holder.certs || [];
            if (Array.isArray(certs)) {
                certificates = certs.filter(cert => cert && cert['@nodeType'] === 'certificate');
            } else if (certs && typeof certs === 'object' && certs['@nodeType'] === 'certificate') {
                certificates = [certs];
            }
        }
        
        // Debug logging for first holder
        if (certificates.length > 0) {
            console.log('[CertificatesOverviewDataService] Extracted certificates for holder:', holder.fullName, {
                count: certificates.length,
                nodeNames: holder['@nodes'],
                firstCert: certificates[0] ? {
                    '@name': certificates[0]['@name'],
                    '@nodeType': certificates[0]['@nodeType'],
                    certificateTitle: certificates[0].certificateTitle
                } : null
            });
        } else {
            console.log('[CertificatesOverviewDataService] No certificates found for holder:', holder.fullName, {
                hasNodes: !!holder['@nodes'],
                nodes: holder['@nodes'],
                hasNewCerts: !!holder.newCerts,
                hasCertificates: !!holder.certificates
            });
        }
        
        return certificates;
    }
}

class CertificatesOverviewFilterManager {
    constructor() {
        this.availableOptions = {
            companies: new Set(),
            programTypes: new Map() // Use Map to store category objects with displayName as key
        };
    }
    
    initializeFilters(allHolders) {
        // Store holders for sidebar navigation
        this.allHolders = allHolders;
        
        // Extract unique values for dropdowns
        allHolders.forEach(({ holder, certificates }) => {
            // Companies
            if (holder.company) {
                const companyName = typeof holder.company === 'object' 
                    ? holder.company.displayName || holder.company.name || holder.company['@name']
                    : holder.company;
                if (companyName) {
                    this.availableOptions.companies.add(companyName);
                }
            }
            if (holder.companyName) {
                this.availableOptions.companies.add(holder.companyName);
            }
            
            // Certificates
            certificates.forEach(cert => {
                // Program Types
                if (cert.programType) {
                    // Console log to inspect structure
                    if (typeof cert.programType === 'object') {
                        console.log('[CertificatesOverviewFilterManager] Program Type object:', JSON.stringify(cert.programType, null, 2));
                    }
                    
                    if (typeof cert.programType === 'object') {
                        // Store the full category object, using displayName as the key
                        const displayName = cert.programType.displayName || cert.programType.name || cert.programType['@name'];
                        if (displayName && !this.availableOptions.programTypes.has(displayName)) {
                            this.availableOptions.programTypes.set(displayName, cert.programType);
                        }
                    } else if (cert.programType) {
                        // Fallback for string values
                        if (!this.availableOptions.programTypes.has(cert.programType)) {
                            this.availableOptions.programTypes.set(cert.programType, { displayName: cert.programType });
                        }
                    }
                }
            });
        });
        
        // Populate dropdowns
        this.populateCompanyDropdown();
        this.populateProgramTypeDropdown();
        
        // Populate sidebar navigation with holders
        this.populateSidebarNavigation();
    }
    
    populateCompanyDropdown() {
        const dropdownEl = document.getElementById('co-filter-company');
        if (!dropdownEl) return;
        
        const list = dropdownEl.querySelector('.dropdown__list');
        if (!list) return;
        
        // Clear existing items except the default "All Companies"
        const defaultItem = list.querySelector('.dropdown__item[data-default="true"]');
        list.innerHTML = '';
        if (defaultItem) {
            const li = document.createElement('li');
            li.appendChild(defaultItem);
            list.appendChild(li);
        }
        
        const sortedCompanies = Array.from(this.availableOptions.companies).sort();
        sortedCompanies.forEach(company => {
            const li = document.createElement('li');
            const button = document.createElement('button');
            button.className = 'dropdown__item';
            button.type = 'button';
            button.dataset.value = company;
            button.textContent = company;
            li.appendChild(button);
            list.appendChild(li);
        });
    }
    
    populateSidebarNavigation() {
        const navList = document.getElementById('co-nav-companies');
        if (!navList) return;
        
        // Sort holders by fullName
        const sortedHolders = this.allHolders
            .map(({ holder }) => holder)
            .sort((a, b) => a.fullName.localeCompare(b.fullName));
        
        // Add "All Holders" option
        const allItem = document.createElement('li');
        allItem.className = 'co__nav-item';
        const allLink = document.createElement('a');
        allLink.href = '#';
        allLink.className = 'co__nav-item-link active';
        allLink.textContent = 'All Holders';
        allLink.dataset.holderId = '';
        allItem.appendChild(allLink);
        navList.appendChild(allItem);
        
        // Add holder links
        sortedHolders.forEach(holder => {
            const li = document.createElement('li');
            li.className = 'co__nav-item';
            const link = document.createElement('a');
            link.href = '#';
            link.className = 'co__nav-item-link';
            link.textContent = holder.fullName;
            link.dataset.holderId = holder.id;
            link.title = holder.fullName; // Tooltip for long names
            li.appendChild(link);
            navList.appendChild(li);
        });
        
        // Set up click handlers
        navList.addEventListener('click', (e) => {
            e.preventDefault();
            const link = e.target.closest('.co__nav-item-link');
            if (!link) return;
            
            // Update active state
            navList.querySelectorAll('.co__nav-item-link').forEach(l => l.classList.remove('active'));
            link.classList.add('active');
            
            // Trigger filter update
            const event = new CustomEvent('co:filterChange', { detail: { holderId: link.dataset.holderId || '' } });
            document.querySelector('.js-module-certificates-overview')?.dispatchEvent(event);
        });
    }
    
    populateProgramTypeDropdown() {
        const dropdownEl = document.getElementById('co-filter-program-type');
        if (!dropdownEl) return;
        
        const list = dropdownEl.querySelector('.dropdown__list');
        if (!list) return;
        
        // Clear existing items except the default "All Program Types"
        const defaultItem = list.querySelector('.dropdown__item[data-default="true"]');
        list.innerHTML = '';
        if (defaultItem) {
            const li = document.createElement('li');
            li.appendChild(defaultItem);
            list.appendChild(li);
        }
        
        // Sort by displayName
        const sortedTypes = Array.from(this.availableOptions.programTypes.entries()).sort((a, b) => {
            return a[0].localeCompare(b[0]); // Sort by displayName (key)
        });
        
        sortedTypes.forEach(([displayName, categoryObj]) => {
            const li = document.createElement('li');
            const button = document.createElement('button');
            button.className = 'dropdown__item';
            button.type = 'button';
            button.dataset.value = displayName;
            button.textContent = displayName;
            li.appendChild(button);
            list.appendChild(li);
        });
    }
    
    getActiveFilters() {
        const getDropdownValue = (id) => {
            const dropdown = document.getElementById(id);
            if (!dropdown) return '';
            const dropdownInstance = dropdown._dropdownInstance;
            return dropdownInstance ? dropdownInstance.currentValue : '';
        };
        
        // Get holder ID from active sidebar nav link
        const activeNavLink = document.querySelector('.co__nav-item-link.active');
        const holderId = activeNavLink ? (activeNavLink.dataset.holderId || '') : '';
        
        return {
            holderId: holderId,
            company: getDropdownValue('co-filter-company'),
            publishStatus: getDropdownValue('co-filter-publish-status'),
            expiration: getDropdownValue('co-filter-expiration'),
            programType: getDropdownValue('co-filter-program-type'),
            certificateType: getDropdownValue('co-filter-certificate-type')
        };
    }
    
    filterHolders(allHolders, filters) {
        return allHolders
            .filter(({ holder }) => {
                // Holder ID filter (from sidebar navigation)
                if (filters.holderId) {
                    return holder.id === filters.holderId;
                }
                return true;
            })
            .filter(({ holder }) => {
                // Company filter (from dropdown)
                if (filters.company) {
                    const companyName = typeof holder.company === 'object'
                        ? holder.company.displayName || holder.company.name || holder.company['@name']
                        : holder.company;
                    return companyName === filters.company || holder.companyName === filters.company;
                }
                return true;
            })
            .map(({ holder, certificates }) => {
                // Filter certificates based on criteria
                const filteredCerts = certificates.filter(cert => {
                    // Publish status filter
                    if (filters.publishStatus) {
                        const isPublished = cert.published === true; // Check if mgnl:lastActivated exists
                        if (filters.publishStatus === 'published' && !isPublished) return false;
                        if (filters.publishStatus === 'unpublished' && isPublished) return false;
                    }
                    
                    // Expiration filter
                    if (filters.expiration && cert.expiryDate) {
                        const expiryDate = new Date(cert.expiryDate);
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        expiryDate.setHours(0, 0, 0, 0);
                        
                        if (filters.expiration === 'expired' && expiryDate > today) return false;
                        if (filters.expiration === 'active' && expiryDate <= today) return false;
                        if (filters.expiration === 'expiring-soon') {
                            const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
                            if (daysUntilExpiry > 30 || daysUntilExpiry <= 0) return false;
                        }
                    }
                    
                    // Program type filter
                    if (filters.programType) {
                        const programTypeName = typeof cert.programType === 'object'
                            ? cert.programType.displayName || cert.programType.name || cert.programType['@name']
                            : cert.programType;
                        if (programTypeName !== filters.programType) return false;
                    }
                    
                    // Certificate type filter
                    if (filters.certificateType && cert.type !== filters.certificateType) {
                        return false;
                    }
                    
                    return true;
                });
                
                // Only include holder if they have matching certificates
                if (filteredCerts.length === 0) return null;
                
                return { holder, certificates: filteredCerts };
            })
            .filter(item => item !== null);
    }
}

class CertificatesOverviewRenderer {
    constructor(container) {
        this.container = container;
        this.resultsContainer = container.querySelector('.co__results');
        this.itemsPerPage = 20;
        this.currentPage = 1;
        this.loadingEl = document.getElementById('co-loading');
        this.loadingProgressBar = document.getElementById('co-loading-progress-bar');
        this.loadingPercentage = document.getElementById('co-loading-percentage');
        this.loadingText = this.container.querySelector('.co__loading-text');
    }
    
    showLoading() {
        if (this.loadingEl) {
            this.loadingEl.style.display = 'block';
        }
    }
    
    hideLoading() {
        if (this.loadingEl) {
            this.loadingEl.style.display = 'none';
        }
    }
    
    updateLoadingProgress(percent, count, message) {
        if (this.loadingProgressBar) {
            this.loadingProgressBar.style.width = `${percent}%`;
        }
        if (this.loadingPercentage) {
            this.loadingPercentage.textContent = `${percent}%`;
        }
        if (this.loadingText && message) {
            this.loadingText.textContent = message;
        }
    }
    
    render(filteredData, filters = {}) {
        if (!this.resultsContainer) return;
        
        if (filteredData.length === 0) {
            this.resultsContainer.innerHTML = '<p class="co__no-results">No certificates found matching the selected filters.</p>';
            return;
        }
        
        // Calculate pagination
        const totalItems = filteredData.length;
        const totalPages = Math.ceil(totalItems / this.itemsPerPage);
        const startIndex = (this.currentPage - 1) * this.itemsPerPage;
        const endIndex = Math.min(startIndex + this.itemsPerPage, totalItems);
        const paginatedData = filteredData.slice(startIndex, endIndex);
        
        let html = '';
        
        // Add company header if filtering by company
        if (filters.company) {
            html += this.renderCompanyHeader(filters.company, totalItems);
        }
        
        // Add results count
        html += this.renderResultsCount(startIndex + 1, endIndex, totalItems);
        
        // Render paginated holders
        paginatedData.forEach(({ holder, certificates }) => {
            html += this.renderHolder(holder, certificates);
        });
        
        // Add pagination controls
        html += this.renderPagination(totalPages, this.currentPage);
        
        this.resultsContainer.innerHTML = html;
        
        // Set up pagination event listeners
        this.setupPaginationListeners();
    }
    
    renderCompanyHeader(companyName, totalHolders) {
        return `
            <div class="co__company-header">
                <h2 class="co__company-title">${this.escapeHtml(companyName)}</h2>
                <p class="co__company-stats">Total number of certified holders per company: <strong>${totalHolders}</strong></p>
            </div>
        `;
    }
    
    renderResultsCount(start, end, total) {
        console.log('[CertificatesOverviewRenderer] Results count:', { start, end, total, type: 'holders' });
        return `<div class="co__results-count p--small">Showing ${start}-${end} of ${total} holders</div>`;
    }
    
    renderPagination(totalPages, currentPage) {
        if (totalPages <= 1) return '';
        
        const pages = this.getPaginationPages(totalPages, currentPage);
        const isFirstPage = currentPage === 1;
        const isLastPage = currentPage === totalPages;
        
        let html = '<div class="co__pagination pagination">';
        html += '<ul class="pagination__list">';
        
        // Previous button
        html += '<li>';
        if (isFirstPage) {
            html += '<button disabled type="button" class="pagination__button pagination__button--prev h5">';
        } else {
            html += `<button type="button" class="pagination__button pagination__button--prev h5" data-page="${currentPage - 1}">`;
        }
        html += '<span class="chevron"></span>';
        html += '<span class="pagination__button-text">&nbsp; Previous</span>';
        html += '</button>';
        html += '</li>';
        
        // Page numbers
        pages.forEach(page => {
            if (page === '..') {
                html += '<li><span class="pagination__page pagination__page--dot h5">..</span></li>';
            } else {
                const isActive = page === currentPage;
                html += `<li>`;
                html += `<button type="button" class="pagination__page h5 ${isActive ? 'pagination__page--active' : ''}" data-page="${page}">${page}</button>`;
                html += `</li>`;
            }
        });
        
        // Next button
        html += '<li>';
        if (isLastPage) {
            html += '<button disabled type="button" class="pagination__button pagination__button--next h5">';
        } else {
            html += `<button type="button" class="pagination__button pagination__button--next h5" data-page="${currentPage + 1}">`;
        }
        html += '<span class="pagination__button-text">Next &nbsp;</span>';
        html += '<span class="chevron"></span>';
        html += '</button>';
        html += '</li>';
        
        html += '</ul>';
        html += '</div>';
        
        return html;
    }
    
    getPaginationPages(totalPages, currentPage) {
        const pages = [];
        const pagesToShow = 5;
        
        if (totalPages <= pagesToShow) {
            // Show all pages if total is less than pagesToShow
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            // Always show first page
            pages.push(1);
            
            if (currentPage > 3) {
                pages.push('..');
            }
            
            // Show pages around current page
            const start = Math.max(2, currentPage - 1);
            const end = Math.min(totalPages - 1, currentPage + 1);
            
            for (let i = start; i <= end; i++) {
                if (i !== 1 && i !== totalPages) {
                    pages.push(i);
                }
            }
            
            if (currentPage < totalPages - 2) {
                pages.push('..');
            }
            
            // Always show last page
            if (totalPages > 1) {
                pages.push(totalPages);
            }
        }
        
        return pages;
    }
    
    setupPaginationListeners() {
        const paginationButtons = this.resultsContainer.querySelectorAll('.pagination button[data-page]');
        paginationButtons.forEach(button => {
            button.addEventListener('click', (e) => {
                const page = parseInt(e.target.dataset.page, 10);
                if (page && page !== this.currentPage) {
                    this.currentPage = page;
                    // Trigger re-render by calling applyFilters from parent
                    const event = new CustomEvent('co:pageChange', { detail: { page } });
                    this.container.dispatchEvent(event);
                }
            });
        });
    }
    
    resetPagination() {
        this.currentPage = 1;
    }
    
    renderHolder(holder, certificates) {
        const companyName = typeof holder.company === 'object'
            ? holder.company.displayName || holder.company.name || holder.company['@name']
            : holder.company || holder.companyName || 'N/A';
        
        let html = `
            <div class="co__holder">
                <div class="co__holder-header">
                    <h3 class="co__holder-name">${this.escapeHtml(holder.fullName)}</h3>
                    <div class="co__holder-meta">
                        <span class="co__holder-company">Company: ${this.escapeHtml(companyName)}</span>
                        ${holder.email ? `<span class="co__holder-email">${this.escapeHtml(holder.email)}</span>` : ''}
                    </div>
                </div>
                <div class="co__certificates">
        `;
        
        certificates.forEach(cert => {
            html += this.renderCertificate(holder, cert);
        });
        
        html += `
                </div>
            </div>
        `;
        
        return html;
    }
    
    renderCertificate(holder, cert) {
        const programTypeName = typeof cert.programType === 'object'
            ? cert.programType.displayName || cert.programType.name || cert.programType['@name']
            : cert.programType || 'N/A';
        
        const issueDate = cert.issueDate ? new Date(cert.issueDate).toLocaleDateString() : 'N/A';
        const expiryDate = cert.expiryDate ? new Date(cert.expiryDate).toLocaleDateString() : 'N/A';
        const isExpired = cert.expiryDate ? new Date(cert.expiryDate) <= new Date() : false;
        
        return `
            <div class="co__certificate">
                <div class="co__certificate-header">
                    <h4 class="co__certificate-title">${this.escapeHtml(cert.title || 'Untitled Certificate')}</h4>
                    <span class="co__certificate-type">${this.escapeHtml(cert.type || 'N/A')}</span>
                </div>
                <div class="co__certificate-details">
                    <div class="co__certificate-detail-item">
                        <strong>Program Type:</strong> ${this.escapeHtml(programTypeName)}
                    </div>
                    <div class="co__certificate-detail-item">
                        <strong>Issued:</strong> ${issueDate}
                    </div>
                    <div class="co__certificate-detail-item">
                        <strong>Expires:</strong> ${expiryDate} ${isExpired ? '<span class="co__expired-badge">Expired</span>' : ''}
                    </div>
                    <div class="co__certificate-detail-item">
                        <strong>Status:</strong> ${cert.published ? '<span class="co__published-badge">Published</span>' : '<span class="co__unpublished-badge">Unpublished</span>'}
                        ${cert.lastActivated ? `<span class="co__last-activated"> (Last: ${new Date(cert.lastActivated).toLocaleDateString()})</span>` : ''}
                    </div>
                </div>
            </div>
        `;
    }
    
    showError(message) {
        if (!this.resultsContainer) return;
        this.resultsContainer.innerHTML = `<p class="co__error">${this.escapeHtml(message)}</p>`;
    }
    
    escapeHtml(text) {
        if (!text) return '';
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }
}

export default function(container) {
    const restUrl = container.dataset.restUrl || '/.rest/delivery/holders';
    
    const dataService = new CertificatesOverviewDataService(restUrl);
    const filterManager = new CertificatesOverviewFilterManager();
    const renderer = new CertificatesOverviewRenderer(container);
    
    let allHolders = [];
    let filteredData = [];
    
    const setupFilterListeners = () => {
        const filterDropdowns = container.querySelectorAll('.co__filter-dropdown');
        filterDropdowns.forEach(dropdownEl => {
            const dropdown = new Dropdown(dropdownEl);
            dropdown.run();
            dropdown.on('valueChange', () => {
                applyFilters();
            });
            // Store reference for getActiveFilters
            dropdownEl._dropdownInstance = dropdown;
        });
        
        // Setup reset filters button
        const resetButton = document.getElementById('co-reset-filters');
        if (resetButton) {
            resetButton.addEventListener('click', () => {
                resetAllFilters();
            });
        }
    };
    
    const resetAllFilters = () => {
        console.log('[CertificatesOverview] Resetting all filters...');
        
        // Reset all dropdown filters
        const filterDropdowns = container.querySelectorAll('.co__filter-dropdown');
        console.log('[CertificatesOverview] Found dropdowns:', filterDropdowns.length);
        
        filterDropdowns.forEach((dropdownEl, index) => {
            const dropdown = dropdownEl._dropdownInstance;
            console.log(`[CertificatesOverview] Dropdown ${index}:`, {
                hasInstance: !!dropdown,
                hasResetMethod: dropdown && typeof dropdown.reset === 'function',
                currentValue: dropdown ? dropdown.currentValue : 'N/A',
                defaultValue: dropdown ? dropdown.defaultValue : 'N/A'
            });
            
            if (dropdown && typeof dropdown.reset === 'function') {
                dropdown.reset();
                console.log(`[CertificatesOverview] Reset dropdown ${index}, new value:`, dropdown.currentValue);
            }
        });
        
        // Reset sidebar navigation to "All Holders"
        const navList = document.getElementById('co-nav-companies');
        if (navList) {
            const allLinks = navList.querySelectorAll('.co__nav-item-link');
            allLinks.forEach(link => link.classList.remove('active'));
            const firstLink = navList.querySelector('.co__nav-item-link');
            if (firstLink) {
                firstLink.classList.add('active');
                console.log('[CertificatesOverview] Reset sidebar to "All Holders"');
            }
        }
        
        console.log('[CertificatesOverview] Applying filters after reset...');
        // Apply filters (which will now be empty/default)
        applyFilters();
    };
    
    const applyFilters = () => {
        // Reset pagination when filters change
        renderer.resetPagination();
        
        const filters = filterManager.getActiveFilters();
        filteredData = filterManager.filterHolders(allHolders, filters);
        console.log('[CertificatesOverview] Filtered data count:', filteredData.length, 'holders');
        console.log('[CertificatesOverview] Total certificates in filtered data:', filteredData.reduce((sum, h) => sum + h.certificates.length, 0));
        renderer.render(filteredData, filters);
    };
    
    // Listen for pagination changes
    container.addEventListener('co:pageChange', (e) => {
        const filters = filterManager.getActiveFilters();
        filteredData = filterManager.filterHolders(allHolders, filters);
        renderer.render(filteredData, filters);
        // Scroll to top of results
        renderer.resultsContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    
    // Listen for filter changes from sidebar
    container.addEventListener('co:filterChange', (e) => {
        applyFilters();
    });
    
    const init = async () => {
        try {
            // Show loading indicator
            renderer.showLoading();
            
            // Fetch all holders data with progress tracking
            allHolders = await dataService.fetchAllHolders((percent, count, message) => {
                renderer.updateLoadingProgress(percent, count, message);
            });
            
            // Initialize filters with available options
            filterManager.initializeFilters(allHolders);
            
            // Hide loading indicator
            renderer.hideLoading();
            
            // Render initial view
            applyFilters();
            
            // Set up filter event listeners
            setupFilterListeners();
        } catch (error) {
            console.error('[CertificatesOverview] Error initializing:', error);
            renderer.hideLoading();
            renderer.showError('Failed to load certificate data. Please try again later.');
        }
    };
    
    init();
}

